import { UseFormReturn, useWatch } from "react-hook-form";

import { useTranslation } from "../../core/translation.core";
import { LocalInputDefaultLocale } from "../locale/locale.comp";
import { EditorSection, Title } from "../reuseables/editor.comp";
import { EditingMode, FancyCheckbox, LabelAndInput, LabelAndSelect } from "../reuseables/form.comp";
import { MembershipFormData } from "../../types/membership";
import { UseMutationResult } from "@tanstack/react-query";
import GenericFormEditor from "../form/genericFormEditor";
import ProductRestrictionForm from "../shipSetting/productRestriction.comp";
import CategoryRestrictionForm from "../shipSetting/categoryRestriction.comp";
import InfoBanner from "../reuseables/infoBanner.comp";

interface Props {
  btnText: string;
  mutation: UseMutationResult<void, Error, MembershipFormData, unknown>;
  methods: UseFormReturn<MembershipFormData, any>;
  editingMode: EditingMode;
}

export default function MembershipEditor(props: Props) {
  const { t, msg } = useTranslation();
  const [discountType, issueMode] = useWatch({ control: props.methods.control, name: ["discountType", "issueMode"] });

  const methods = props.methods;

  return (
    <GenericFormEditor methods={methods} mutation={props.mutation} btnText={props.btnText}>
      <EditorSection>
        <Title>{t(msg.product.generalInfo)}</Title>
        <div className="space-y-2">
          <LocalInputDefaultLocale register={methods.register} fieldName="name" label={t(msg.general.name)} />
          <LabelAndInput
            register={methods.register}
            fieldName="code"
            label={t(msg.category.code)}
            labelCss=""
            topLabelCss="block"
            registerOptions={{ required: true, disabled: props.editingMode === EditingMode.edit }}
            errors={methods.formState.errors}
          />
          <LabelAndSelect
            methods={methods}
            fieldName="issueMode"
            topLabelCss="block"
            label={t(msg.membership.activationMethod)}
            setDefault={false}
            options={[
              { label: t(msg.product.auto), value: "auto" },
              { label: t(msg.product.manual), value: "manual" },
            ]}
          />

          <LabelAndInput
            register={methods.register}
            fieldName="validityPeriod"
            label={t(msg.membership.validityPeriod)}
            labelCss=""
            topLabelCss="block"
            type="number"
            registerOptions={{ required: true }}
            errors={methods.formState.errors}
          />

          <FancyCheckbox value={true} register={methods.register("default")} labelId="membership-default-tier">
            <span className="dark:text-slate-300">{t(msg.membership.defaultTier)}</span>
          </FancyCheckbox>
        </div>
      </EditorSection>

      {issueMode === "auto" ? (
        <EditorSection>
          <Title>{t(msg.membership.issueCriteria)}</Title>
          <AutoIssueCriteria methods={methods} />
        </EditorSection>
      ) : null}

      <EditorSection>
        <Title>{t(msg.discount.discount)}</Title>

        <div className="space-y-2">
          {/* Membership Discount  should only take percentage. No fixed values */}

          {/* <LabelAndSelect
            methods={methods}
            fieldName="discountType"
            topLabelCss="block"
            label={t(msg.discount.discountType)}
            setDefault={false}
            options={[
              { label: t(msg.discount.percentage), value: "percentage" },
              { label: t(msg.discount.fixedAmt), value: "fixed" },
            ]}
          />  */}

          <div>
            <LabelAndInput
              register={methods.register}
              fieldName="discount.per"
              label={t(msg.discount.percentage)}
              labelCss=""
              type="number"
              step="0.01"
              registerOptions={{ required: discountType === "percentage" }}
              errors={methods.formState.errors}
            />
          </div>

          <LabelAndInput
            register={methods.register}
            fieldName="rewardMultiplier"
            label={t(msg.membership.rewardMultiplier)}
            labelCss=""
            topLabelCss="block"
            type="number"
            step="0.01"
            registerOptions={{ min: 1 }}
            errors={methods.formState.errors}
            infoComponents={<InfoBanner message={t(msg.membership.rewardMultiplierInfo)} />}
          />

          {/* {discountType === "fixed" ? (
            <LabelAndInput
              register={methods.register}
              fieldName="discount.amt"
              label={t(msg.discount.fixedAmt)}
              labelCss=""
              topLabelCss="block"
              type="number"
              registerOptions={{ required: discountType === "fixed" }}
              errors={methods.formState.errors}
            />
          ) : null} */}
        </div>
      </EditorSection>

      <EditorSection>
        <Title>{t(msg.coupon.eligibility)}</Title>
        <div className="space-y-6">
          <ProductRestrictionForm<MembershipFormData>
            methods={methods}
            typeFieldName="productRestriction.type"
            idsFieldName="productRestriction.restrictToIds"
          />
          <CategoryRestrictionForm<MembershipFormData>
            methods={methods}
            typeFieldName="categoryRestriction.type"
            idsFieldName="categoryRestriction.restrictToIds"
          />
        </div>
      </EditorSection>
    </GenericFormEditor>
  );
}

function AutoIssueCriteria({ methods }: { methods: UseFormReturn<MembershipFormData> }) {
  const { t, msg } = useTranslation();
  return (
    <div className="flex flex-col gap-2">
      <LabelAndSelect
        methods={methods}
        fieldName="autoIssueCriteria.placement"
        topLabelCss="block"
        label={t(msg.coupon.placement)}
        setDefault={false}
        options={[
          { label: t(msg.placement.eshop), value: "online" },
          { label: t(msg.placement.retail), value: "pos" },
          { label: t(msg.placement.both), value: "both" },
        ]}
      />

      <LabelAndInput
        step="0.01"
        register={methods.register}
        fieldName="autoIssueCriteria.minTriggerAmt"
        label={t(msg.membership.minTriggerAmount)}
        labelCss=""
        topLabelCss="block"
        type="number"
        errors={methods.formState.errors}
        registerOptions={{ required: true }}
      />

      <LabelAndInput
        step="1"
        register={methods.register}
        fieldName="autoIssueCriteria.minTriggerPeriod"
        label={t(msg.membership.orderDurationDay)}
        labelCss=""
        topLabelCss="block"
        type="number"
        errors={methods.formState.errors}
        registerOptions={{ required: true }}
      />

      {/*  <div className="text-xs flex flex-col gap-4">
        <label>
          <span className={"text-xs font-semibold mr-2"}>{t(msg.membership.firstTimeIssue)}</span>
          <input type="checkbox" {...methods.register("autoIssueCriteria.firstTimeReg")} />
        </label>
      </div> //TODO */}
    </div>
  );
}

function SubmitButton({ editingMode }: { editingMode: EditingMode }) {
  const { t, msg } = useTranslation();

  function getSubmitBtnText() {
    switch (editingMode) {
      case EditingMode.create:
        return t(msg.membership.create);
      case EditingMode.edit:
        return t(msg.membership.edit);
    }
  }

  return (
    <div className="my-8">
      <input type="submit" value={getSubmitBtnText()} />
    </div>
  );
}
